import React, { useMemo } from 'react';
import { ClipboardCheck, ClipboardList, ShieldAlert } from 'lucide-react';
import { useAppContext } from '../context/AppContext';
import { formatCurrency, formatDate, getStatusText, getStatusTone } from '../utils/helpers';
import Badge from './common/Badge';
import { t } from '../i18n';

const Approvals = () => {
  const { currentUser, purchaseOrders, purchaseRequisitions, suppliers, findUserById, renewablePortfolio } =
    useAppContext();

  const isPurchasingManager = ['purchasing_manager', 'super_user'].includes(
    currentUser?.role || ''
  );

  const {
    pendingOrders,
    upcomingOrders,
    pendingRequisitions,
    recentRequisitions
  } = useMemo(() => {
    const pendingOrdersList = purchaseOrders.filter(
      (order) => order.status === 'pendiente_compras'
    );
    const upcomingOrdersList = purchaseOrders
      .filter((order) => order.status === 'aprobada')
      .slice(0, 5);

    const pendingRequisitionStatuses = new Set(['pendiente_compra', 'pendiente_controller']);
    const pendingRequisitionsList = purchaseRequisitions.filter((req) =>
      pendingRequisitionStatuses.has(req.status)
    );

    const recentRequisitionsList = purchaseRequisitions
      .filter((req) => req.status === 'convertida' || req.status === 'aprobada')
      .slice(0, 5);

    return {
      pendingOrders: pendingOrdersList,
      upcomingOrders: upcomingOrdersList,
      pendingRequisitions: pendingRequisitionsList,
      recentRequisitions: recentRequisitionsList
    };
  }, [purchaseOrders, purchaseRequisitions]);

  const pendingDevelopmentApprovals = useMemo(() => {
    return renewablePortfolio.filter(
      (project) => !project.approval?.projectManager || !project.approval?.purchasingManager
    );
  }, [renewablePortfolio]);

  const getSupplierName = (supplierId) =>
    suppliers.find((supplier) => supplier.id === supplierId)?.name || 'Proveedor sin definir';

  const getRequester = (userId) => {
    if (!userId) return '—';
    const user = findUserById(userId);
    return user?.name || 'Usuario';
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">{t('Aprobaciones y revisiones pendientes')}</h1>
          <p className="text-sm text-slate-500">{t(
            'Visualiza requisiciones y órdenes de compra pendientes de tu aprobación o recientemente resueltas.'
          )}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
        <section className="bg-white border border-slate-200 rounded-3xl p-6 shadow-sm space-y-4">
          <div className="flex items-center gap-3">
            <ClipboardCheck className="w-6 h-6 text-neutral-400" />
            <div>
              <p className="text-sm font-semibold text-slate-900">{t('Órdenes de compra pendientes')}</p>
              <p className="text-xs text-slate-500">{t('Revisiones en espera de aprobación por la gerencia de compras.')}</p>
            </div>
          </div>
          {pendingOrders.length === 0 ? (
            <p className="text-sm text-slate-500">{t('No tienes órdenes de compra pendientes de aprobación.')}</p>
          ) : (
            <div className="space-y-2">
              {pendingOrders.map((order) => (
                <div
                  key={order.id}
                  className="border border-slate-100 rounded-2xl px-4 py-3 bg-slate-50 text-sm text-slate-600"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-semibold text-slate-800">{t(order.id)}</p>
                      <p className="text-xs text-slate-500">
                        {t(getSupplierName(order.supplierId))} · {t(order.currency) || 'CLP'}
                      </p>
                    </div>
                    <p className="text-sm font-semibold text-emerald-600">
                      {formatCurrency(order.totalValue || 0)}
                    </p>
                  </div>
                  <p className="mt-1 text-xs text-slate-500">{t('Creada el')} {formatDate(order.createdDate)} {t('· Estado:')} {t(getStatusText(order.status))}
                  </p>
                </div>
              ))}
            </div>
          )}
        </section>

        <section className="bg-white border border-slate-200 rounded-3xl p-6 shadow-sm space-y-4">
          <div className="flex items-center gap-3">
            <ClipboardList className="w-6 h-6 text-neutral-400" />
            <div>
              <p className="text-sm font-semibold text-slate-900">{t('Requisiciones pendientes')}</p>
              <p className="text-xs text-slate-500">{t('Solicitudes que requieren derivación a comprador o revisión técnica.')}</p>
            </div>
          </div>
          {pendingRequisitions.length === 0 ? (
            <p className="text-sm text-slate-500">{t('No existen requisiciones pendientes de gestión en este momento.')}</p>
          ) : (
            <div className="space-y-2">
              {pendingRequisitions.map((req) => (
                <div
                  key={req.id}
                  className="border border-slate-100 rounded-2xl px-4 py-3 bg-slate-50 text-sm text-slate-600"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-semibold text-slate-800">{t(req.id)}</p>
                      <p className="text-xs text-slate-500">{t(req.title)}</p>
                    </div>
                    <Badge tone={getStatusTone(req.status)}>{t(getStatusText(req.status))}</Badge>
                  </div>
                  <p className="mt-1 text-xs text-slate-500">{t('Solicitante:')} {t(getRequester(req.requesterId))} {t('· Fecha:')}{' '}
                    {formatDate(req.createdDate)}
                  </p>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
        <section className="bg-white border border-slate-200 rounded-3xl p-6 shadow-sm space-y-4">
          <div className="flex items-center gap-3">
            <ShieldAlert className="w-6 h-6 text-neutral-400" />
            <div>
              <p className="text-sm font-semibold text-slate-900">{t('Órdenes aprobadas recientemente')}</p>
              <p className="text-xs text-slate-500">{t('Detalle de las últimas órdenes validadas por el área de compras.')}</p>
            </div>
          </div>
          {upcomingOrders.length === 0 ? (
            <p className="text-sm text-slate-500">{t('Aún no se han aprobado órdenes de compra recientes.')}</p>
          ) : (
            <div className="space-y-2">
              {upcomingOrders.map((order) => (
                <div
                  key={order.id}
                  className="border border-slate-100 rounded-2xl px-4 py-3 bg-white text-sm text-slate-600"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-semibold text-slate-800">{t(order.id)}</p>
                      <p className="text-xs text-slate-500">{t(getSupplierName(order.supplierId))}</p>
                    </div>
                    <p className="text-sm font-semibold text-emerald-600">
                      {formatCurrency(order.totalValue || 0)}
                    </p>
                  </div>
                  <p className="mt-1 text-xs text-slate-500">{t('Aprobada el')} {order.approvedDate ? formatDate(order.approvedDate) : '—'}
                  </p>
                </div>
              ))}
            </div>
          )}
        </section>

        <section className="bg-white border border-slate-200 rounded-3xl p-6 shadow-sm space-y-4">
          <div className="flex items-center gap-3">
            <ClipboardCheck className="w-6 h-6 text-neutral-400" />
            <div>
              <p className="text-sm font-semibold text-slate-900">{t('Requisiciones finalizadas')}</p>
              <p className="text-xs text-slate-500">{t('Últimas requisiciones convertidas en órdenes o aprobadas por compras.')}</p>
            </div>
          </div>
          {recentRequisitions.length === 0 ? (
            <p className="text-sm text-slate-500">{t('No se registran requisiciones convertidas recientemente.')}</p>
          ) : (
            <div className="space-y-2">
              {recentRequisitions.map((req) => (
                <div
                  key={req.id}
                  className="border border-slate-100 rounded-2xl px-4 py-3 bg-white text-sm text-slate-600"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-semibold text-slate-800">{t(req.id)}</p>
                      <p className="text-xs text-slate-500">{t(req.title)}</p>
                    </div>
                    <Badge tone={getStatusTone(req.status)}>{t(getStatusText(req.status))}</Badge>
                  </div>
                  <p className="mt-1 text-xs text-slate-500">{t('Solicitante:')} {t(getRequester(req.requesterId))} {t('· Fecha:')}{' '}
                    {formatDate(req.createdDate)}
                  </p>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>

      <section className="bg-white border border-slate-200 rounded-3xl p-6 shadow-sm space-y-4">
        <div className="flex items-center gap-3">
          <ShieldAlert className="w-6 h-6 text-neutral-400" />
          <div>
            <p className="text-sm font-semibold text-slate-900">{t('Aprobaciones de desarrollo pendientes')}</p>
            <p className="text-xs text-slate-500">{t(
              'Stage-gates de proyectos renovables que aun no completan aprobacion dual.'
            )}</p>
          </div>
        </div>
        {pendingDevelopmentApprovals.length === 0 ? (
          <p className="text-sm text-slate-500">{t('No hay pendientes en gates de desarrollo.')}</p>
        ) : (
          <div className="space-y-2">
            {pendingDevelopmentApprovals.map((project) => (
              <div
                key={project.id}
                className="border border-slate-100 rounded-2xl px-4 py-3 bg-slate-50 text-sm text-slate-600"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-semibold text-slate-800">{t(project.name)}</p>
                    <p className="text-xs text-slate-500">{t(project.stageLabel)}</p>
                  </div>
                  <p className="text-xs text-slate-500">{t('PM:')} {project.approval?.projectManager ? 'OK' : t('Pendiente')} {t('· Compras:')}{' '}
                    {project.approval?.purchasingManager ? 'OK' : t('Pendiente')}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {!isPurchasingManager && (
        <div className="px-4 py-3 bg-warning-50 border border-warning-100 rounded-2xl text-xs text-warning-700">{t(
          'Este módulo está optimizado para la Gerencia de Compras. Algunas acciones pueden estar limitadas según tu perfil.'
        )}</div>
      )}
    </div>
  );
};

export default Approvals;
